import { useCallback, useEffect, useState, type SyntheticEvent } from 'react';
import { Link } from 'react-router';
import { api, type Physician, type User } from '../api.ts';
import { Notice } from '../components/Notice.tsx';
import { copy, formatDateTime } from '../copy/en-CA.ts';
import { describeAccountFailure as describe, describePhysicianChoices, type PhysicianChoice } from '../describe.ts';
import { useSession } from '../session.tsx';

const c = copy.admin.users;

function PhysicianOptions({ choices }: { choices: PhysicianChoice[] }) {
  return (
    <>
      <option value="">{c.noPhysician}</option>
      {choices.map((p) => (
        <option key={p.id} value={p.id} disabled={p.disabled}>
          {p.label}
        </option>
      ))}
    </>
  );
}

export function AdminUsersPage() {
  const { user: me } = useSession();
  const [users, setUsers] = useState<User[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [physicianId, setPhysicianId] = useState('');
  const [physicians, setPhysicians] = useState<Physician[]>([]);
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ name: string; password: string } | null>(null);

  const [rowNotice, setRowNotice] = useState<{ id: string; kind: 'error' | 'success'; text: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const [{ users: list }, { physicians: roster }] = await Promise.all([api.admin.listUsers(), api.admin.listPhysicians()]);
      setUsers(list);
      // The whole roster: an account linked to a deactivated physician still shows them (describePhysicianChoices).
      setPhysicians(roster);
      setLoadError(null);
    } catch (err) {
      setLoadError(describe(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const create = async (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);
    setCreated(null);
    setCreating(true);
    try {
      const { user, temporaryPassword } = await api.admin.createUser({ email, displayName, isAdmin, physicianId: physicianId || null });
      setCreated({ name: user.displayName, password: temporaryPassword });
      setEmail('');
      setDisplayName('');
      setIsAdmin(false);
      setPhysicianId('');
      await load();
    } catch (err) {
      setFormError(describe(err));
    } finally {
      setCreating(false);
    }
  };

  const update = async (u: User, patch: { isAdmin?: boolean; active?: boolean; physicianId?: string | null }) => {
    setRowNotice(null);
    try {
      await api.admin.updateUser(u.id, patch);
      await load();
    } catch (err) {
      setRowNotice({ id: u.id, kind: 'error', text: describe(err) });
    }
  };

  const reset = async (u: User) => {
    if (!window.confirm(c.confirmReset(u.displayName))) return;
    setRowNotice(null);
    try {
      const { temporaryPassword } = await api.admin.resetPassword(u.id);
      setRowNotice({ id: u.id, kind: 'success', text: `${c.resetDone(u.displayName)} ${temporaryPassword}` });
      await load();
    } catch (err) {
      setRowNotice({ id: u.id, kind: 'error', text: describe(err) });
    }
  };

  const deactivate = async (u: User) => {
    if (!window.confirm(c.confirmDeactivate(u.displayName))) return;
    await update(u, { active: false });
  };

  return (
    <>
      <div className="page-title">
        <h1>{c.title}</h1>
        <p>{c.intro}</p>
      </div>

      <form
        className="card"
        onSubmit={(e) => {
          void create(e);
        }}
        aria-labelledby="add-user"
      >
        <h2 id="add-user">{c.add}</h2>
        <div className="row">
          <div className="field grow">
            <label htmlFor="new-name">{c.displayName}</label>
            <input id="new-name" className="input" required value={displayName} onChange={(e) => { setDisplayName(e.target.value); }} />
          </div>
          <div className="field grow">
            <label htmlFor="new-email">{c.email}</label>
            <input id="new-email" className="input" type="email" required value={email} onChange={(e) => { setEmail(e.target.value); }} />
          </div>
        </div>
        <div className="row">
          <div className="field">
            <label htmlFor="new-physician">{c.physician}</label>
            <select
              id="new-physician"
              className="input"
              aria-describedby="new-physician-note"
              value={physicianId}
              onChange={(e) => {
                setPhysicianId(e.target.value);
              }}
            >
              <PhysicianOptions choices={describePhysicianChoices(physicians, users ?? [], null)} />
            </select>
            <p id="new-physician-note" className="muted small">
              {c.physicianNote}
            </p>
          </div>
          <label className="checkbox">
            <input type="checkbox" checked={isAdmin} onChange={(e) => { setIsAdmin(e.target.checked); }} />
            {c.isAdmin}
          </label>
        </div>
        {formError ? <Notice kind="error">{formError}</Notice> : null}
        {created ? (
          <Notice kind="success">
            {c.created(created.name)} <span className="mono">{created.password}</span>
          </Notice>
        ) : null}
        <div>
          <button type="submit" className="btn primary" disabled={creating}>
            {creating ? c.creating : c.create}
          </button>
        </div>
      </form>

      {loadError ? <Notice kind="error">{loadError}</Notice> : null}
      {users ? (
        <div className="table-region" role="region" aria-label={c.title} tabIndex={0}>
          <table>
            <thead>
              <tr>
                <th>{c.columns.name}</th>
                <th>{c.columns.email}</th>
                <th>{c.columns.role}</th>
                <th>{c.columns.status}</th>
                <th>{c.columns.lastSignIn}</th>
                <th>{c.columns.actions}</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td>
                    <strong>{u.displayName}</strong>
                    <div>
                      <label className="visually-hidden" htmlFor={`link-${u.id}`}>
                        {c.physician}
                      </label>
                      {/* Wider than the shared small-select's 220 px, so "Henry Albrecht (deactivated)" is not cut off. */}
                      <select
                        id={`link-${u.id}`}
                        className="input small-select"
                        style={{ maxWidth: '18rem' }}
                        value={u.physicianId ?? ''}
                        onChange={(e) => void update(u, { physicianId: e.target.value || null })}
                      >
                        <PhysicianOptions choices={describePhysicianChoices(physicians, users, u)} />
                      </select>
                    </div>
                  </td>
                  <td>{u.email}</td>
                  <td>
                    <span className={`badge ${u.role}`}>{copy.roles[u.role]}</span>{' '}
                    {u.isAdmin && u.physicianId ? <span className="badge physician">{copy.roles.physician}</span> : null}
                  </td>
                  <td>
                    {!u.active ? (
                      <span className="badge inactive">{c.status.inactive}</span>
                    ) : u.mustChangePassword ? (
                      <span className="badge pending">{c.status.pending}</span>
                    ) : (
                      <span className="badge">{c.status.active}</span>
                    )}
                  </td>
                  <td className="muted">{formatDateTime(u.lastSignInAt)}</td>
                  <td>
                    <div className="row">
                      {u.id !== me?.id ? (
                        <button type="button" className="btn small" onClick={() => void update(u, { isAdmin: !u.isAdmin })}>
                          {u.isAdmin ? c.revokeAdmin : c.grantAdmin}
                        </button>
                      ) : null}
                      {/* A reset signs the person out everywhere: on one's own row, that is
                          on the spot. Their own password is changed the usual way instead. */}
                      {u.id !== me?.id ? (
                        <button type="button" className="btn small" onClick={() => void reset(u)}>
                          {c.reset}
                        </button>
                      ) : (
                        <Link to="/password" className="small">
                          {c.ownPassword}
                        </Link>
                      )}
                      {u.id !== me?.id ? (
                        u.active ? (
                          <button type="button" className="btn small danger" onClick={() => void deactivate(u)}>
                            {c.deactivate}
                          </button>
                        ) : (
                          <button type="button" className="btn small" onClick={() => void update(u, { active: true })}>
                            {c.reactivate}
                          </button>
                        )
                      ) : null}
                    </div>
                    {rowNotice?.id === u.id ? (
                      <div style={{ marginTop: '0.5rem' }}>
                        <Notice kind={rowNotice.kind}>{rowNotice.text}</Notice>
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  );
}
